<template>
  <BackNav :title="$t('successOrder.title')" />
  <div class="container">
    <div class="order-detail" id="printOrder">
      <van-row>
        <van-col span="12">{{ $t('successOrder.validFor') }}</van-col>
        <van-col span="12">{{ orderDetail.lotteryName }}</van-col>
      </van-row>
      <van-row>
        <van-col span="8">{{ $t('successOrder.mobile') }}</van-col>
        <van-col span="16">{{ orderDetail.mobile }}</van-col>
      </van-row>
      <van-row>
        <van-col span="8">{{ $t('successOrder.date') }}</van-col>
        <van-col span="16">{{ $filters.formatDate(orderDetail.createdAt) }}</van-col>
      </van-row>
      <van-row>
        <van-col span="8">{{ $t('successOrder.invoice') }}</van-col>
        <van-col span="16">{{ orderDetail.orderSn }}</van-col>
      </van-row>

      <van-row>
        <van-col span="8">{{ $t('successOrder.paga') }}</van-col>
        <van-col span="16">
          <template v-if="hasR">M:{{ orderDetail.mAmount }} R:{{ orderDetail.rAmount }}</template>
          <template v-else>M:{{ orderDetail.mAmount }}</template>
        </van-col>
      </van-row>
      <ul class="theader" style="display: flex; flex-direction: row; margin-top: 2.66667vw">
        <li class="th" style="flex: 1">{{ $t('successOrder.colNum') }}</li>
        <li class="th" style="flex: 1">{{ $t('successOrder.colAmount') }}</li>
        <li class="th" style="flex: 1" v-if="hasR">{{ $t('successOrder.colReve') }}</li>
      </ul>
      <ul class="tbody" style="display: flex; flex-direction: row" v-for="(item, index) of orderDetail.orderDetailList" :key="index">
        <li class="td" style="flex: 1">{{ item.num }}</li>
        <li class="td" style="flex: 1">{{ item.mAmount }}</li>
        <li class="td" style="flex: 1" v-if="hasR">{{ item.rAmount }}</li>
      </ul>
      <van-row style="margin-top: 2.66667vw">
        <van-col span="8">{{ $t('successOrder.total') }}</van-col>
        <van-col span="16">₡{{ orderDetail.amount }}</van-col>
      </van-row>
      <div class="text" style="margin-top: 6.66667vw">{{ $t('successOrder.note1') }}</div>
      <div class="text">{{ $t('successOrder.note2') }}</div>
      <div class="text">{{ $t('successOrder.note3') }}</div>
      <div class="text">{{ $t('successOrder.note4') }}</div>
      <div class="text">{{ $t('successOrder.sellerSign') }}</div>
      <div class="text" style="margin-top: 2.66667vw; text-indent: 5.33333vw">{{ $t('successOrder.thanks') }}</div>
    </div>

    <!-- 底部操作按钮:继续下单 / WhatsApp 发送 / 打印 -->
    <div class="action-buttons">
      <van-button block round class="brand-btn action-btn continue" @click="continueOrderHandler">
        <template #icon><van-icon name="plus" /></template>
        {{ $t('successOrder.continueOrder') }}
      </van-button>
      <van-button block round class="action-btn whatsapp" @click="sendWhatsAppHandler">
        <template #icon><van-icon name="chat" /></template>
        {{ $t('successOrder.sendWhatsApp') }}
      </van-button>
      <van-button block round class="action-btn print" v-print="'#printOrder'">
        <template #icon><van-icon name="printer" /></template>
        {{ $t('successOrder.print') }}
      </van-button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { OrderService } from '@/services';
import BackNav from '@/components/BackNav';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const orderDetail = ref({});

// 是否该显示 R 列:只要有任何一条明细里 rAmount > 0 就显示
// (数据驱动,历史订单 & R禁用的订单都自动正确)
const hasR = computed(() => {
  const list = orderDetail.value.orderDetailList || [];
  return list.some((x) => Number(x.rAmount) > 0);
});

const getOrderDetail = async () => {
  const { result } = await OrderService.getOrderByIdApi(route.query.orderId);
  orderDetail.value = result;
};

// 继续下单 → 回到首页
const continueOrderHandler = () => {
  router.push('/');
};

// 拼装 WhatsApp 发送内容(纯文本,WhatsApp 里可读)
const buildWhatsAppText = () => {
  const o = orderDetail.value;
  const lines = [];
  lines.push(`*${t('successOrder.title')}*`);
  lines.push('');
  lines.push(`${t('successOrder.validFor')}: ${o.lotteryName || '-'}`);
  lines.push(`${t('successOrder.date')}: ${formatDateString(o.createdAt)}`);
  lines.push(`${t('successOrder.invoice')}: ${o.orderSn || '-'}`);
  lines.push(hasR.value
    ? `${t('successOrder.paga')}: M:${o.mAmount} R:${o.rAmount}`
    : `${t('successOrder.paga')}: M:${o.mAmount}`);
  lines.push('');
  lines.push('----------------------------');
  // 明细头
  const header = hasR.value
    ? `${t('successOrder.colNum')}   ${t('successOrder.colAmount')}   ${t('successOrder.colReve')}`
    : `${t('successOrder.colNum')}   ${t('successOrder.colAmount')}`;
  lines.push(header);
  lines.push('----------------------------');
  // 明细行
  const list = o.orderDetailList || [];
  for (const it of list) {
    const numStr = String(it.num).padStart(2, '0');
    lines.push(hasR.value
      ? `${numStr}     ${it.mAmount}      ${it.rAmount}`
      : `${numStr}     ${it.mAmount}`);
  }
  lines.push('----------------------------');
  lines.push(`${t('successOrder.total')}: ₡${o.amount}`);
  return lines.join('\n');
};

// 极简的日期字符串化(避免 iOS Safari 兼容问题)
const formatDateString = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// WhatsApp 发送(打开分享对话框,不指定接收方)
const sendWhatsAppHandler = () => {
  const text = buildWhatsAppText();
  // wa.me 是 WhatsApp 官方通用短链接,手机上会拉起 App,PC 上会打开 Web 版
  const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank');
};

onMounted(() => {
  getOrderDetail();
});
</script>

<style lang="scss" scoped>
.container {
  padding: 20px;
  box-sizing: border-box;
  padding-bottom: 40px;

  .order-detail {
    background: #fff;
    width: 100%;
    height: auto;
    border-radius: 16px;
    padding: 32px 24px;
    box-sizing: border-box;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  }
  .theader {
    display: flex;
    flex-direction: row;
    .th {
      flex: 1;
    }
  }
  .tbody {
    display: flex;
    flex-direction: row;
    .td {
      flex: 1;
    }
  }
  .text {
    font-size: 30px;
    margin-bottom: 20px;
  }
}

.action-buttons {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.action-btn {
  height: 96px !important;
  font-size: 30px !important;
  font-weight: 600 !important;
  letter-spacing: 2px !important;

  :deep(.van-button__icon) {
    font-size: 34px;
    margin-right: 8px;
  }
}
.action-btn.continue {
  // brand-btn class 已带渐变紫色
}
.action-btn.whatsapp {
  background: linear-gradient(135deg, #25d366, #128c7e) !important;
  color: #fff !important;
  border: none !important;
  box-shadow: 0 8px 24px rgba(37, 211, 102, 0.28) !important;
}
.action-btn.print {
  background: #fff !important;
  color: #667eea !important;
  border: 1px solid #667eea !important;
}
</style>
